import { useEffect, useState } from 'react';

import { api } from '../api/client';
import type { TaxCode } from '../types';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function TaxCodes() {
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchTaxCodes() {
      try {
        const data = await api.taxCodes.list();
        setTaxCodes(data);
      } catch (error) {
        console.error('Failed to fetch tax codes:', error);
      } finally {
        setLoading(false);
      }
    }
    void fetchTaxCodes();
  }, []);

  const formatRate = (rate: number) => {
    return `${(rate * 100).toFixed(rate < 0.1 ? 3 : 1)}%`;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading tax codes...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tax Codes</h1>
          <p className="text-sm text-muted-foreground">Canadian sales tax configuration (GST, HST, PST, QST)</p>
        </div>
        <Button variant="default" size="sm">
          <i className="bi bi-plus"></i> Add Tax Code
        </Button>
      </div>

      {/* Tax Codes List */}
      <Card>
        <CardHeader>
          <CardTitle>Tax Codes ({taxCodes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Code</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Name</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Type</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Rate</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Province</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {taxCodes.map((taxCode) => (
                  <tr key={taxCode.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{taxCode.code}</code></td>
                    <td className="py-3 px-4">{taxCode.name}</td>
                    <td className="py-3 px-4">
                      <Badge variant={taxCode.type === 'HST' ? 'default' : taxCode.type === 'GST' ? 'secondary' : 'default'}>
                        {taxCode.type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 font-mono">{formatRate(taxCode.rate)}</td>
                    <td className="py-3 px-4">{taxCode.province ?? 'All'}</td>
                    <td className="py-3 px-4">
                      <Badge variant={taxCode.is_active ? 'secondary' : 'default'}>
                        {taxCode.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm">Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-info-circle"></i> About Canadian Sales Taxes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Canadian businesses collect various sales taxes depending on their location:
          </p>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li><strong>GST</strong> - Federal Goods and Services Tax (5%)</li>
            <li><strong>HST</strong> - Harmonized Sales Tax (varies by province)</li>
            <li><strong>PST</strong> - Provincial Sales Tax (varies by province)</li>
            <li><strong>QST</strong> - Quebec Sales Tax (9.975%)</li>
          </ul>
          <p className="text-sm text-muted-foreground mt-4">
            Tax codes are used to calculate taxes on sales and purchases, and for reporting to the CRA.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}